import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { PiggyBank, Save, Loader2, PauseCircle, PlayCircle } from "lucide-react";
import { API, authHeaders } from "@/constants/roles";
import { fmtMoney, fmtNum } from "@/utils/currency";

const formatError = (detail) => {
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || String(e)).join(" ");
  return "Something went wrong.";
};

export const AdminBudget = () => {
  const [budget, setBudget] = useState(null);
  const [total, setTotal] = useState("");
  const [reserve, setReserve] = useState("");
  const [saving, setSaving] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [staffSummary, setStaffSummary] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(() => {
    axios
      .get(`${API}/admin/budget`, { headers: authHeaders() })
      .then((r) => {
        setBudget(r.data);
        setTotal(String(r.data.total_budget));
        setReserve(String(r.data.reserve_threshold));
      })
      .catch((err) => {
        if (err.response?.status === 401 || err.response?.status === 403) navigate("/login");
        else toast.error("Could not load budget.");
      });
    axios
      .get(`${API}/admin/staff-compensation/summary`, { headers: authHeaders() })
      .then((r) => setStaffSummary(r.data))
      .catch(() => {});
  }, [navigate]);

  useEffect(load, [load]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await axios.put(
        `${API}/admin/budget`,
        { total_budget: parseFloat(total) || 0, reserve_threshold: parseFloat(reserve) || 0 },
        { headers: authHeaders() }
      );
      setBudget(data);
      toast.success("Budget updated.");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const toggleProduction = async () => {
    setToggling(true);
    try {
      await axios.post(`${API}/admin/production/toggle`, { paused: !budget.production_paused }, { headers: authHeaders() });
      toast.success(budget.production_paused ? "Production resumed." : "Production paused — workers can't claim new jobs.");
      load();
    } catch {
      toast.error("Could not update production status.");
    } finally {
      setToggling(false);
    }
  };

  if (!budget) {
    return <p data-testid="budget-loading" className="py-20 text-center text-sm font-medium text-[var(--pv-text-4)]">Loading budget...</p>;
  }

  const committedPct = budget.total_budget > 0 ? Math.min(100, (budget.committed / budget.total_budget) * 100) : 0;
  const owedPct = budget.total_budget > 0 ? Math.min(100, (budget.owed / budget.total_budget) * 100) : 0;
  const paidPct = budget.total_budget > 0 ? Math.min(100, (budget.paid / budget.total_budget) * 100) : 0;

  const cards = [
    { label: "Total budget", value: budget.total_budget, testid: "budget-total", tone: "text-[var(--pv-text)]" },
    { label: "Committed (active jobs)", value: budget.committed, testid: "budget-committed", tone: "text-blue-600" },
    { label: "Owed (approved, unpaid)", value: budget.owed, testid: "budget-owed", tone: "text-amber-400", sub: (budget.owed_staff ?? 0) > 0 ? `incl. staff pay ${fmtMoney(budget.owed_staff)}` : null },
    { label: "Paid out", value: budget.paid, testid: "budget-paid", tone: "text-emerald-400", sub: (budget.paid_staff ?? 0) > 0 ? `incl. staff pay ${fmtMoney(budget.paid_staff)}` : null },
    { label: "Available to commit", value: budget.available, testid: "budget-available", tone: budget.available < 0 ? "text-rose-400" : "text-cyan-400" },
    { label: "Burn (last 30 days)", value: budget.burn_rate_30d, testid: "budget-burn", tone: "text-[var(--pv-text)]" },
  ];

  return (
    <div data-testid="admin-budget">
      <h1 className="font-display text-2xl font-bold tracking-tight text-[var(--pv-text)]">Budget</h1>
      <p className="mt-1 text-sm text-[var(--pv-text-4)]">Committed funds are reserved the moment a worker claims a job — before anything is paid.</p>

      <div className={`mt-6 flex flex-wrap items-center justify-between gap-3 rounded-3xl border px-6 py-5 ${budget.production_paused ? "border-rose-500/20 bg-rose-500/10" : "border-emerald-500/20 bg-emerald-500/10"}`}>
        <div className={`flex items-center gap-2.5 text-sm font-semibold ${budget.production_paused ? "text-rose-300" : "text-emerald-300"}`}>
          {budget.production_paused ? <PauseCircle className="h-5 w-5" /> : <PlayCircle className="h-5 w-5" />}
          {budget.production_paused
            ? `Production is paused${budget.pause_reason === "budget_reserve" ? " — budget reserve reached" : ""}. Workers can't claim new jobs.`
            : "Production is active — workers can claim jobs."}
        </div>
        <button
          data-testid="production-toggle-btn"
          onClick={toggleProduction}
          disabled={toggling}
          className={`flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-bold text-white transition-colors disabled:opacity-60 ${budget.production_paused ? "bg-emerald-500 hover:bg-emerald-600" : "bg-rose-500 hover:bg-rose-600"}`}
        >
          {toggling && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
          {budget.production_paused ? "Resume production" : "Pause production"}
        </button>
      </div>

      <div className="mt-6 grid grid-cols-2 gap-4 lg:grid-cols-3">
        {cards.map((c) => (
          <div key={c.testid} data-testid={c.testid} className="rounded-3xl border border-[var(--pv-border)] bg-[var(--pv-card)] p-6 ">
            <p className={`font-display text-3xl font-bold tracking-tight ${c.tone}`}>{fmtMoney(c.value)}</p>
            <p className="mt-1 text-[11px] font-semibold uppercase tracking-wider text-[var(--pv-text-4)]">{c.label}</p>
            {c.sub && <p className="mt-1 text-[10px] font-semibold text-fuchsia-300/80">{c.sub}</p>}
          </div>
        ))}
      </div>

      {(budget.paid_staff > 0 || budget.owed_staff > 0 || staffSummary?.active_count > 0) && (
        <div data-testid="budget-staff-card" className="mt-6 rounded-3xl border border-fuchsia-500/20 bg-fuchsia-500/5 p-6 sm:p-7">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-fuchsia-300/80">Spending split — production vs staff</p>
          <div className="mt-4 grid gap-4 sm:grid-cols-3">
            <div>
              <p data-testid="budget-split-production" className="font-display text-xl font-bold text-[var(--pv-text)]">{fmtMoney(budget.paid_production ?? (budget.paid - (budget.paid_staff || 0)))}</p>
              <p className="mt-0.5 text-[11px] font-semibold text-[var(--pv-text-4)]">Animator / job production costs (paid)</p>
            </div>
            <div>
              <p data-testid="budget-split-staff" className="font-display text-xl font-bold text-fuchsia-300">{fmtMoney(budget.paid_staff || 0)}</p>
              <p className="mt-0.5 text-[11px] font-semibold text-[var(--pv-text-4)]">Staff / management costs (paid)</p>
            </div>
            <div>
              <p data-testid="budget-split-total" className="font-display text-xl font-bold text-[var(--pv-text)]">{fmtMoney(budget.paid)}</p>
              <p className="mt-0.5 text-[11px] font-semibold text-[var(--pv-text-4)]">Total business spending (paid)</p>
            </div>
          </div>
          {staffSummary && staffSummary.active_count > 0 && (
            <p data-testid="budget-staff-projection" className="mt-4 border-t border-fuchsia-500/15 pt-3 text-xs font-medium text-[var(--pv-text-4)]">
              Active staff arrangements: <b className="text-fuchsia-300">{staffSummary.active_count}</b> · projected{" "}
              <b className="text-fuchsia-300">{fmtMoney(staffSummary.weekly_total)}/week</b> ·{" "}
              <b className="text-fuchsia-300">{fmtMoney(staffSummary.monthly_total)}/month</b>
              {staffSummary.due_now > 0 && <> · <b className="text-amber-300">{staffSummary.due_now} payment{staffSummary.due_now === 1 ? "" : "s"} due now</b></>}
            </p>
          )}
        </div>
      )}

      <div className="mt-6 rounded-3xl border border-[var(--pv-border)] bg-[var(--pv-card)] p-6  sm:p-8">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-[var(--pv-text-4)]">Where the budget sits</p>
        <div className="mt-4 h-4 w-full overflow-hidden rounded-full bg-[var(--pv-raised)]" data-testid="budget-bar">
          <div className="flex h-full">
            <div className="bg-emerald-500 transition-all" style={{ width: `${paidPct}%` }} title="Paid" />
            <div className="bg-amber-400 transition-all" style={{ width: `${owedPct}%` }} title="Owed" />
            <div className="bg-blue-400 transition-all" style={{ width: `${committedPct}%` }} title="Committed" />
          </div>
        </div>
        <div className="mt-3 flex flex-wrap gap-4 text-xs font-semibold text-[var(--pv-text-4)]">
          <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-emerald-500" /> Paid</span>
          <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-amber-400" /> Owed</span>
          <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-blue-400" /> Committed</span>
          <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-[var(--pv-raised)]" /> Available</span>
        </div>
      </div>

      <form data-testid="budget-form" onSubmit={save} className="mt-6 rounded-3xl border border-[var(--pv-border)] bg-[var(--pv-card)] p-6  sm:p-8">
        <div className="flex items-center gap-2.5">
          <PiggyBank className="h-5 w-5 text-cyan-400" />
          <h2 className="font-display text-lg font-bold text-[var(--pv-text)]">Budget settings</h2>
        </div>
        <div className="mt-5 grid gap-5 sm:grid-cols-2">
          <div>
            <label htmlFor="budget-total-input" className="mb-1.5 block text-xs font-semibold text-[var(--pv-text-2)]">Total production budget (USD)</label>
            <input
              id="budget-total-input"
              data-testid="budget-total-input"
              type="number"
              min="0"
              step="0.01"
              value={total}
              onChange={(e) => setTotal(e.target.value)}
              className="w-full rounded-2xl border border-[var(--pv-border)] bg-[var(--pv-card)] px-4 py-3 text-sm font-medium text-[var(--pv-text)] focus:border-cyan-400 focus:outline-none focus:ring-4 focus:ring-cyan-500/10"
            />
          </div>
          <div>
            <label htmlFor="budget-reserve-input" className="mb-1.5 block text-xs font-semibold text-[var(--pv-text-2)]">Reserve threshold (USD)</label>
            <input
              id="budget-reserve-input"
              data-testid="budget-reserve-input"
              type="number"
              min="0"
              step="0.01"
              value={reserve}
              onChange={(e) => setReserve(e.target.value)}
              className="w-full rounded-2xl border border-[var(--pv-border)] bg-[var(--pv-card)] px-4 py-3 text-sm font-medium text-[var(--pv-text)] focus:border-cyan-400 focus:outline-none focus:ring-4 focus:ring-cyan-500/10"
            />
            <p className="mt-2 text-xs text-[var(--pv-text-4)]">When available funds would drop below this, production auto-pauses.</p>
          </div>
        </div>
        <button
          data-testid="budget-save-btn"
          type="submit"
          disabled={saving}
          className="mt-6 flex items-center gap-2 rounded-full bg-cyan-500 px-6 py-3 text-sm font-bold text-white shadow-lg shadow-cyan-500/25 transition-colors hover:bg-cyan-600 disabled:opacity-60"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Save budget
        </button>
      </form>
    </div>
  );
};
